import React, { useState, useEffect } from "react";
import search from "../../../assets/svg/search.svg";
import ProductImage from "../../../assets/images/our-product.png";
import { GetAllOrder } from "../../../Services/OrderService";
import { MoveFromOrderToCart } from "../../../Services/CartService";
import { getImageUrl, handleImageError } from "../../../utils/imageUtils";

const ByAgain = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingOrderItemId, setLoadingOrderItemId] = useState(null); // ✅ track loading button

  // ✅ Fetch orders (used by both initial load & search)
  const fetchOrders = async (term = "") => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      console.error("User ID not found");
      setLoading(false);
      return;
    }
    try {
      const params = { userId: parseInt(userId) };
      if (term) params.filter = term;
      const response = await GetAllOrder(params);
      setOrders(response?.data?.orders || []); // handle null safely
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]); // fallback to empty
    } finally {
      setLoading(false);
    }
  };

  // ✅ Buy Again button handler
  const handleBuyAgain = async (orderItemId) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        console.error("User ID not found");
        setLoading(false);
        return;
      }

      setLoadingOrderItemId(orderItemId); // start loader on button

      const params = {
        orderItemId: parseInt(orderItemId),
        userId: userId,
      };

      const response = await MoveFromOrderToCart(params);
      if (response?.status) {
        // ✅ Update cart count in header
        window.dispatchEvent(new Event('cartUpdated'));
      }
    } catch (error) {
      console.error("Error moving item:", error);
    } finally {
      setLoadingOrderItemId(null); // stop loader
    }
  };

  // ✅ Initial Load
  useEffect(() => {
    fetchOrders();
  }, []);

  // ✅ Auto-search on input typing (with debounce)
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchOrders(searchTerm);
    }, 400); // wait 400ms after user stops typing
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  return (
    <>
      <div className="main-content">
        <div className="d-flex align-items-center justify-content-between mb_28 order-content-box">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <h2 className="mb-0 fw_600 fs_24 lh-1">Your Orders</h2>
            <div className="drop-down-content d-md-none">
              <select className="form-select select-transparent">
                <option selected>Past 3 Months</option>
                <option>2025</option>
                <option>2024</option>
              </select>
            </div>
          </div>

          <div className="form-input search-field">
            <input
              type="text"
              className="form-control"
              placeholder="Search Your Order"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <img src={search} alt="Search" className="search-icon" />
          </div>
        </div>

        {/* ✅ If no orders found */}
        {loading ? (
          <div className="text-center py_50">
            <h5 className="fs_18 fw_400 text-muted">Fetching your orders...</h5>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py_50">
            <h5 className="fs_20 fw_500 text-muted">No Orders Found</h5>
          </div>
        ) : (
          <div className="order-list-box" style={{ overflowY: "auto", maxHeight: "400px" }}>
            {orders.map((order) => (
              <div key={order.orderId} className="order-card">
                <p className="fs_18 fw_400 lh-1 text-color mb_10">
                  <b>Order ID - </b># {order.orderGuid}
                </p>
                <hr style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }} />
                <div className="order-grid-list">
                  {order.products.map((product) => (
                    <div
                      key={product.productId}
                      className="cart-grid d-flex align-items-center justify-content-between gap-2"
                    >
                      <div className="d-flex align-items-start">
                        <div className="img-wrapper">
                          <img
                            className="img-fluid"
                            src={getImageUrl(product.image, ProductImage)}
                            onError={(e) => handleImageError(e, ProductImage)}
                            alt="cart product"
                          />
                        </div>
                        <div>
                          <div className="mb_10">
                            <h6 className="fs_24 fw_700 text-color mb-0">
                              {product.productName} {product.weight > 0 && `(${product.weight}KG)`}
                            </h6>
                          </div>
                          <div className="tag-list d-flex align-items-center flex-wrap gap-2">
                            {product.attributes.subProducts.map((subProduct) => (
                              <div key={subProduct.id} className="tag py_10 px_10 fs_12 text-color">
                                {subProduct.name} {subProduct.weight}
                              </div>
                            ))}
                            {product.attributes.attributes.map((attr) => (
                              <div key={attr.id} className="tag py_10 px_10 fs_12 text-color">
                                {attr.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ✅ Buy Again Button with Loader */}
                      <div>
                        <button
                          className="button btn-solid text-uppercase"
                          onClick={() => handleBuyAgain(product.orderItemId)}
                          disabled={loadingOrderItemId === product.productId}
                        >
                          {loadingOrderItemId === product.productId ? (
                            <div
                              className="spinner-border spinner-border-sm text-light"
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          ) : (
                            "Add To Cart"
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ByAgain;